import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  getDevices,
  deleteDevice,
  deleteDevices,
  DeleteDeviceModal,
  getSite,
  getIntercomsSummary,
  SyncDeviceModal,
  SyncAllDevicesModal,
  syncDevice,
  syncDevices,
  syncAllDevices,
  triggerDeviceStatusCheck,
  getDeviceSipStatuses,
  getDeviceCameraSnapshotUrl,
  prewarmDeviceCameraSnapshot,
  discoverDeviceIps,
  releaseDoor,
} from "./deviceService";
import { getAuthToken } from "views/pages/hucs/util/helpers";

import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import {
  hasResourcePermission,
  hasAnyResourcePermission,
  PERMISSIONS,
  RESOURCES,
} from "utils/permissions";
import { ThemeContext } from "contexts/ThemeContext.jsx";

// reactstrap components
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Media,
  Table,
  Container,
  Row,
  Col,
  UncontrolledTooltip,
  Form,
  FormGroup,
  InputGroup,
  InputGroupText,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";

import HusariaHeader from "views/pages/components/HusariaHeader";

const { SearchBar } = Search;

const pagination = paginationFactory({
  page: 1,
  alwaysShowAllBtns: true,
  showTotal: true,
  withFirstAndLast: false,
  sizePerPageRenderer: ({ options, currSizePerPage, onSizePerPageChange }) => (
    <div className="dataTables_length" id="datatable-basic_length">
      <label>
        Show{" "}
        {
          <select
            name="datatable-basic_length"
            aria-controls="datatable-basic"
            className="form-control form-control-sm"
            onChange={(e) => onSizePerPageChange(e.target.value)}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        }{" "}
        entries.
      </label>
    </div>
  ),
});

const INTERCOMS_TABLE_CACHE_TTL_MS = 2 * 60 * 1000;
const INTERCOMS_TABLE_CACHE_PREFIX = "hrms_intercoms_table_cache:";
const SUPPORTED_DEVICE_ACTION_MODELS = new Set([
  "2N IP Style",
  "2N IP Verso",
  "2N IP Solo",
  "Akuvox S539",
]);

const supportsCurrentDeviceActions = (deviceModel) => (
  SUPPORTED_DEVICE_ACTION_MODELS.has(String(deviceModel || "").trim())
);

const getDeviceImage = (deviceModel) => {
  if (deviceModel === "2N IP Style") {
    return require("assets/img/devices/2nipstyle.png");
  }

  if (deviceModel === "2N IP Verso") {
    return require("assets/img/devices/2nipverso.jpg");
  }

  if (deviceModel === "2N IP Solo") {
    return require("assets/img/devices/2nipsolo.jpg");
  }

  if (deviceModel === "Akuvox S539") {
    return require("assets/img/devices/Akuvox-S539.png");
  }

  return null;
};

const getIntercomsTableCacheKey = (siteUuid) => `${INTERCOMS_TABLE_CACHE_PREFIX}${siteUuid}`;

const readIntercomsTableCache = (siteUuid) => {
  if (!siteUuid || typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(getIntercomsTableCacheKey(siteUuid));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const cachedAt = Number(parsed?.cachedAt || 0);
    const isFresh = cachedAt && Date.now() - cachedAt <= INTERCOMS_TABLE_CACHE_TTL_MS;

    if (!isFresh) {
      window.sessionStorage.removeItem(getIntercomsTableCacheKey(siteUuid));
      return null;
    }

    return {
      devices: Array.isArray(parsed?.devices) ? parsed.devices : [],
      summary: parsed?.summary || {},
      sipStatuses: parsed?.sipStatuses || {},
      cachedAt,
    };
  } catch (err) {
    console.warn("Failed to read intercoms table cache:", err);
    try {
      window.sessionStorage.removeItem(getIntercomsTableCacheKey(siteUuid));
    } catch (_) { }
    return null;
  }
};

const writeIntercomsTableCache = (siteUuid, { devices = [], summary = {}, sipStatuses = {} } = {}) => {
  if (!siteUuid || typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(
      getIntercomsTableCacheKey(siteUuid),
      JSON.stringify({
        cachedAt: Date.now(),
        devices: Array.isArray(devices) ? devices : [],
        summary: summary || {},
        sipStatuses: sipStatuses || {},
      })
    );
  } catch (err) {
    console.warn("Failed to write intercoms table cache:", err);
  }
};

const clearIntercomsTableCache = (siteUuid) => {
  if (!siteUuid || typeof window === "undefined") return;

  try {
    window.sessionStorage.removeItem(getIntercomsTableCacheKey(siteUuid));
  } catch (_) { }
};


function HrmsDevices() {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const [devices, setDevices] = useState([]);
  const [siteId, setSiteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [devicesLoadedForSite, setDevicesLoadedForSite] = useState(false);
  const [deviceStatusesLoading, setDeviceStatusesLoading] = useState(false);
  const [sipStatusesLoading, setSipStatusesLoading] = useState(false);
  const [sipStatusesLoaded, setSipStatusesLoaded] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [deviceToSync, setDeviceToSync] = useState(null);
  const [syncModal, setSyncModal] = useState(false);
  const [syncAllModal, setSyncAllModal] = useState(false);
  const [bulkSyncModal, setBulkSyncModal] = useState(false);
  const [intercomsSummary, setIntercomsSummary] = useState({});
  const [syncLabel, setSyncLabel] = useState("Syncing enterphones…");
  const [isUpdatingBackground, setIsUpdatingBackground] = useState(false);
  const [syncResultsModal, setSyncResultsModal] = useState(false);
  const [syncResults, setSyncResults] = useState({
    title: "",
    summary: "",
    errors: [],
    warnings: [],
    published: [],
    successes: [],
  });
  const [pendingSyncResults, setPendingSyncResults] = useState(null);
  const [syncBanner, setSyncBanner] = useState({
    show: false,
    type: "success", // "success" | "danger" | "warning"
    message: "",
  });
  const [pendingSyncBanner, setPendingSyncBanner] = useState(null);

  // Camera preview state
  const [livePreviewModal, setLivePreviewModal] = useState(false);
  const [previewDevice, setPreviewDevice] = useState(null);
  const [showInternalPreview, setShowInternalPreview] = useState(false);
  const [previewRefreshKey, setPreviewRefreshKey] = useState(Date.now());
  const [internalImgError, setInternalImgError] = useState(false);
  const [forceFreshPreview, setForceFreshPreview] = useState(false);
  const [liveAutoRefreshEnabled, setLiveAutoRefreshEnabled] = useState(true);

  const [discoveringIps, setDiscoveringIps] = useState(false);
  const [releaseDoorModal, setReleaseDoorModal] = useState(false);
  const [deviceToReleaseDoor, setDeviceToReleaseDoor] = useState(null);
  const [releaseDoorLoading, setReleaseDoorLoading] = useState(null);

  const userData = React.useMemo(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  }, []);

  const INTERCOMS_RESOURCE = RESOURCES.HRMS_INTERCOMS;

  const canView = hasResourcePermission(
    userData,
    INTERCOMS_RESOURCE,
    PERMISSIONS.VIEW
  );

  const canCreate = hasResourcePermission(
    userData,
    INTERCOMS_RESOURCE,
    PERMISSIONS.CREATE
  );

  const canEdit = hasResourcePermission(
    userData,
    INTERCOMS_RESOURCE,
    PERMISSIONS.EDIT
  );

  const canDelete = hasResourcePermission(
    userData,
    INTERCOMS_RESOURCE,
    PERMISSIONS.DELETE
  );

  const canSync = hasResourcePermission(
    userData,
    INTERCOMS_RESOURCE,
    PERMISSIONS.DEVICE_CONTROL
  );

  const canPerformActions = hasAnyResourcePermission(
    userData,
    INTERCOMS_RESOURCE,
    [
      PERMISSIONS.CREATE,
      PERMISSIONS.EDIT,
      PERMISSIONS.DELETE,
      PERMISSIONS.DEVICE_CONTROL,
    ]
  );

  const syncableDevices = devices.filter((device) => (
    supportsCurrentDeviceActions(device.device_model)
  ));

  const selectedSyncableDeviceIds = selectedDevices.filter((deviceUuid) => (
    syncableDevices.some((device) => String(device.device_uuid) === String(deviceUuid))
  ));

  const infoStyle = {
    backgroundColor: isDark ? "#1f2937" : "#fff",
    borderRadius: "1rem",
    overflow: "hidden",
  }

  const sipStatusesRef = useRef({});
  const sipFetchStartedRef = useRef(false);
  const sipFetchInFlightRef = useRef(false);
  const intercomsSummaryRef = useRef({});
  const currentSiteRef = useRef(null);
  const initialStatusLoadRunRef = useRef(0);
  const tableScrollRef = useRef(null);
  const syncingRef = useRef(false);

  const initialStatusLoadPending = Boolean(
    siteId && (loading || deviceStatusesLoading || !devicesLoadedForSite || sipStatusesLoading || !sipStatusesLoaded)
  );

  const tableActionsBlocked = Boolean(
    !siteId ||
    initialStatusLoadPending ||
    refreshing ||
    syncing ||
    isUpdatingBackground ||
    discoveringIps ||
    releaseDoorLoading ||
    livePreviewModal
  );

  const rowActionsBlocked = Boolean(
    !siteId ||
    initialStatusLoadPending ||
    refreshing ||
    isUpdatingBackground ||
    discoveringIps ||
    releaseDoorLoading ||
    livePreviewModal
  );

  const tableActionBlockLabel = refreshing
    ? "Updating device and SIP status…"
    : initialStatusLoadPending
      ? "Loading device and SIP status…"
      : discoveringIps
        ? "Discovering IPs…"
        : releaseDoorLoading
          ? "Releasing door…"
          : syncing
            ? syncLabel
            : isUpdatingBackground
              ? "Updating background…"
              : livePreviewModal
                ? "Live preview open…"
                : !siteId
                  ? "Select a site to manage enterphones."
                  : "Actions temporarily unavailable…";

  const markDeviceStatusRowsLoading = useCallback(() => {
    setDevices((prevDevices) =>
      (prevDevices || []).map((device) => ({
        ...device,
        device_status_loading: true,
      }))
    );
  }, []);

  const releaseTableActionLocks = useCallback(() => {
    setLoading(false);
    setRefreshing(false);
    setDeviceStatusesLoading(false);
    setDevicesLoadedForSite(true);
    setSipStatusesLoading(false);
    setSipStatusesLoaded(true);
  }, []);

  useEffect(() => {
    syncingRef.current = syncing;
  }, [syncing]);

  useEffect(() => {
    if (tableActionsBlocked && selectedDevices.length > 0) {
      setSelectedDevices([]);
    }
  }, [tableActionsBlocked, selectedDevices.length]);

  const prewarmInternalSnapshots = async (deviceList) => {
    const onlineDevices = (deviceList || []).filter((d) => {
      return (
        supportsCurrentDeviceActions(d.device_model) &&
        d.device_uuid &&
        d.last_checkin &&
        Date.now() - new Date(d.last_checkin).getTime() <= 10 * 60 * 1000
      );
    });

    const firstFew = onlineDevices.slice(0, 4);

    for (const device of firstFew) {
      try {
        prewarmDeviceCameraSnapshot(device.device_uuid, {
          source: "internal",
          width: 320,
          height: 240,
        });
      } catch (err) {
        console.warn("Prewarm failed for device", device.device_uuid, err);
      }
    }
  };

  const applySipStatusesToDevices = useCallback((deviceList, sipStatuses = {}) => {
    const statusesByIp = Object.values(sipStatuses || {}).reduce((acc, item) => {
      if (item?.device_ip) {
        acc[item.device_ip] = item;
      }
      return acc;
    }, {});

    return (deviceList || []).map((d) => {
      const online =
        d.last_checkin &&
        Date.now() - new Date(d.last_checkin).getTime() <= 10 * 60 * 1000;

      const statusObj =
        sipStatuses[String(d.device_uuid)] ||
        statusesByIp[d.device_ip] ||
        null;

      const liveSipStatus = statusObj?.sip_registration_status;
      // Do not use HUCS extension assignment to decide whether SIP status is applicable.
      // The backend checks /phone/status for every device with an IP, and a device can
      // still have an active SIP registration even if no HUCS extension is assigned.
      const fallbackSipStatus = d.device_ip ? "Loading..." : "N/A";

      return {
        ...d,
        device_status_loading: false,
        online_status: online ? 1 : 0,
        sip_registration_status: liveSipStatus || fallbackSipStatus,
        config_sync_status: d.config_sync_status || "never",
        last_sync_success_at: d.last_sync_success_at || null,
      };
    });
  }, []);

  const formatDateTime = (value) => {
    if (!value) return "NEVER SYNCED";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "NEVER SYNCED";
    return date.toLocaleString();
  };

  const getSyncBadgeMeta = (status) => {
    switch ((status || "").toLowerCase()) {
      case "synced":
        return { dotClass: "bg-success", label: "SYNCED" };
      case "out_of_sync":
        return { dotClass: "bg-warning", label: "OUT OF SYNC" };
      case "pending":
        return { dotClass: "bg-info", label: "PENDING" };
      case "failed":
        return { dotClass: "bg-danger", label: "FAILED" };
      case "never":
      default:
        return { dotClass: "bg-secondary", label: "NEVER SYNCED" };
    }
  };

  // LOAD DEVICES
  const loadDevices = useCallback(async () => {
    if (!siteId) return;

    try {
      const data = await getDevices(siteId);
      const summary = await getIntercomsSummary(siteId);
      const safeData = data || [];

      const devicesWithStatus = applySipStatusesToDevices(
        safeData,
        sipStatusesRef.current
      );
      const summaryValue = summary || {};

      setDevices(devicesWithStatus);
      setIntercomsSummary(summaryValue);
      intercomsSummaryRef.current = summaryValue;
      setDevicesLoadedForSite(true);

      const hasSipStatuses = Object.keys(sipStatusesRef.current || {}).length > 0;
      if (safeData.length === 0 || hasSipStatuses) {
        writeIntercomsTableCache(siteId, {
          devices: devicesWithStatus,
          summary: summaryValue,
          sipStatuses: sipStatusesRef.current,
        });
      }

      if (safeData.length === 0) {
        setSipStatusesLoaded(true);
        setSipStatusesLoading(false);
      }
    } catch (err) {
      console.error("Error fetching devices:", err);
      setDevices([]);
      setIntercomsSummary({});
      setDevicesLoadedForSite(true);
      setSipStatusesLoaded(true);
      setSipStatusesLoading(false);
    } finally {
      setLoading(false);
    }
  }, [siteId, applySipStatusesToDevices]);

  const reloadDevicesAfterSync = useCallback(async () => {
    if (!siteId) return;

    clearIntercomsTableCache(siteId);

    try {
      await loadDevices();
    } finally {
      // A sync only needs to reload DB-backed table data.
      // It should not leave the table waiting for device/SIP status checks.
      releaseTableActionLocks();
    }
  }, [siteId, loadDevices, releaseTableActionLocks]);

  const fetchSipStatusesForCurrentSite = useCallback(
    async ({ force = false, blockActions = true } = {}) => {
      if (!siteId || livePreviewModal || syncingRef.current || isUpdatingBackground) return;

      if (!force && sipFetchStartedRef.current) return;
      if (!force && sipFetchInFlightRef.current) return;

      sipFetchStartedRef.current = true;
      sipFetchInFlightRef.current = true;

      if (blockActions) {
        setSipStatusesLoading(true);
        setSipStatusesLoaded(false);
      }

      if (force) {
        sipStatusesRef.current = {};

        if (blockActions) {
          setDevices((prevDevices) =>
            (prevDevices || []).map((d) => ({
              ...d,
              sip_registration_status: d.device_ip ? "Loading..." : "N/A",
            }))
          );
        }
      }

      try {
        const sipStatusRes = await getDeviceSipStatuses(siteId);
        const statuses = sipStatusRes?.statuses || {};

        sipStatusesRef.current = statuses;

        setDevices((prevDevices) => {
          const updatedDevices = applySipStatusesToDevices(prevDevices || [], statuses);

          writeIntercomsTableCache(siteId, {
            devices: updatedDevices,
            summary: intercomsSummaryRef.current || {},
            sipStatuses: statuses,
          });

          return updatedDevices;
        });
      } catch (err) {
        console.warn("Failed to fetch SIP statuses:", err);

        setDevices((prevDevices) =>
          (prevDevices || []).map((d) => ({
            ...d,
            sip_registration_status: d.device_ip ? "UNKNOWN" : "N/A",
          }))
        );
      } finally {
        sipFetchInFlightRef.current = false;

        if (blockActions) {
          setSipStatusesLoading(false);
          setSipStatusesLoaded(true);
        }
      }
    },
    [siteId, livePreviewModal, isUpdatingBackground, applySipStatusesToDevices]
  );

  const openSyncResultsModal = ({
    title,
    summary,
    errors = [],
    warnings = [],
    published = [],
  }) => {
    const visibleErrors = filterVisibleSyncIssues(errors);
    const visibleWarnings = filterVisibleSyncIssues(warnings);
    const successes = getSuccessfulSyncItems(published, visibleErrors, visibleWarnings);

    if (
      visibleErrors.length === 0 &&
      visibleWarnings.length === 0 &&
      successes.length === 0
    ) {
      return;
    }

    setPendingSyncResults({
      title: title || "Sync Results",
      summary: summary || "",
      errors: visibleErrors,
      warnings: visibleWarnings,
      published,
      successes,
    });
  };

  const closeSyncResultsModal = () => {
    setSyncResultsModal(false);
    setPendingSyncResults(null);
    setSyncResults({
      title: "",
      summary: "",
      errors: [],
      warnings: [],
      published: [],
      successes: [],
    });
  };

  const isIgnorableSyncIssue = (item) => {
    const msg = [
      item?.error,
      item?.message,
      item?.description,
      item?.param,
      item?.topic,
      JSON.stringify(item || {}),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return (
      msg.includes("invalid parameter value") &&
      msg.includes("code") &&
      msg.includes("12") &&
      msg.includes("blob-dir_new")
    );
  };

  const filterVisibleSyncIssues = (items = []) => {
    return (items || []).filter((item) => !isIgnorableSyncIssue(item));
  };

  const getSuccessfulSyncItems = (published = [], errors = [], warnings = []) => {
    const visibleErrors = filterVisibleSyncIssues(errors);

    // Warnings should not make a device count as failed.
    // Only real errors should remove it from the success list.
    const failedDeviceKeys = new Set(
      visibleErrors
        .map((item) => item?.device_uuid || item?.device_ip || item?.device_name)
        .filter(Boolean)
    );

    return (published || []).filter((item) => {
      const key = item?.device_uuid || item?.device_ip || item?.device_name;

      return (
        key &&
        item?.success !== false &&
        !failedDeviceKeys.has(key)
      );
    });
  };

  const getUniqueIssueDeviceCount = (errors = [], warnings = []) => {
    const keys = new Set();

    [...errors, ...warnings].forEach((item) => {
      const key = item?.device_uuid || item?.device_ip || item?.device_name;
      if (key) keys.add(key);
    });

    return keys.size;
  };

  const pluralize = (count, singular, plural = `${singular}s`) => {
    return count === 1 ? singular : plural;
  };

  const formatIssuePart = (deviceCount, issueCount, issueLabel) => {
    if (issueCount <= 0) return null;

    const safeDeviceCount = Number(deviceCount || 0);

    if (issueLabel === "error") {
      if (safeDeviceCount > 0) {
        return `${safeDeviceCount} ${pluralize(safeDeviceCount, "enterphone")} failed with ${issueCount} total ${pluralize(issueCount, "error")}`;
      }

      return `${issueCount} total ${pluralize(issueCount, "error")} reported`;
    }

    if (safeDeviceCount > 0) {
      return `${safeDeviceCount} ${pluralize(safeDeviceCount, "enterphone")} completed with ${issueCount} total ${pluralize(issueCount, "warning")}`;
    }

    return `${issueCount} total ${pluralize(issueCount, "warning")} reported`;
  };

  const formatSyncResultsSummary = ({
    successCount = 0,
    errorCount = 0,
    warningCount = 0,
    errorDeviceCount = 0,
    warningDeviceCount = 0,
  } = {}) => {
    const syncText =
      successCount > 0
        ? `Successfully synced ${successCount} ${pluralize(successCount, "enterphone")}.`
        : "No enterphones were synced.";

    const issueParts = [
      formatIssuePart(errorDeviceCount, errorCount, "error"),
      formatIssuePart(warningDeviceCount, warningCount, "warning"),
    ].filter(Boolean);

    if (issueParts.length === 0) {
      return successCount > 0
        ? `${syncText} No issues were reported.`
        : "No sync results were returned.";
    }

    return `${syncText} ${issueParts.join(". ")}.`;
  };

  const getSyncIssueText = (item) => {
    return [
      item?.error,
      item?.message,
      item?.description,
      item?.detail,
      item?.topic,
      JSON.stringify(item || {}),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
  };

  const isTimeoutSyncIssue = (item) => {
    const msg = getSyncIssueText(item);

    return (
      msg.includes("timed out") ||
      msg.includes("timeout") ||
      msg.includes("waiting for proxy response")
    );
  };

  const getSyncResultAdvice = (errors = [], warnings = []) => {
    const issues = [...(errors || []), ...(warnings || [])];

    if (issues.some(isTimeoutSyncIssue)) {
      return "One or more enterphones timed out while waiting for the proxy response. Confirm the saved device IP address, click refresh to update the table statuses, then try syncing again.";
    }

    return "";
  };

  const renderSyncResultAdvice = () => {
    const advice = getSyncResultAdvice(syncResults.errors, syncResults.warnings);

    if (!advice) return null;

    return (
      <div
        className="mb-3"
        style={{
          padding: "0.75rem 1rem",
          borderRadius: "0.5rem",
          backgroundColor: isDark ? "#3a321f" : "#fffbeb",
          border: `1px solid ${isDark ? "#d69e2e" : "#fcd34d"}`,
          color: isDark ? "#fef3c7" : "#92400e",
          fontWeight: 500,
        }}
      >
        <strong>Suggested next step:</strong> {advice}
      </div>
    );
  };

  // INIT SITE
  useEffect(() => {
    const syncSiteFromStorage = () => {
      const { site_uuid } = getSite();
      setSiteId(site_uuid || null);
    };

    syncSiteFromStorage();

    window.addEventListener("siteChanged", syncSiteFromStorage);
    window.addEventListener("appContextChanged", syncSiteFromStorage);
    window.addEventListener("storage", syncSiteFromStorage);

    return () => {
      window.removeEventListener("siteChanged", syncSiteFromStorage);
      window.removeEventListener("appContextChanged", syncSiteFromStorage);
      window.removeEventListener("storage", syncSiteFromStorage);
    };
  }, []);

  useEffect(() => {
    if (!siteId) {
      initialStatusLoadRunRef.current += 1;
      currentSiteRef.current = null;
      sipStatusesRef.current = {};
      sipFetchStartedRef.current = false;
      sipFetchInFlightRef.current = false;
      setDevices([]);
      setDevicesLoadedForSite(false);
      setDeviceStatusesLoading(false);
      setSipStatusesLoaded(false);
      setSipStatusesLoading(false);
      setRefreshing(false);
      setLoading(false);
      return;
    }

    // Only run the heavy initial status flow when the selected site actually changes.
    // This avoids retriggering proxy status checks when unrelated UI state changes.
    if (currentSiteRef.current === siteId) return;

    currentSiteRef.current = siteId;
    const runId = initialStatusLoadRunRef.current + 1;
    initialStatusLoadRunRef.current = runId;

    const cachedTable = readIntercomsTableCache(siteId);
    if (cachedTable) {
      sipStatusesRef.current = cachedTable.sipStatuses || {};
      sipFetchStartedRef.current = true;
      sipFetchInFlightRef.current = false;
      intercomsSummaryRef.current = cachedTable.summary || {};

      setDevices(cachedTable.devices || []);
      setIntercomsSummary(cachedTable.summary || {});
      setSelectedDevices([]);
      setDevicesLoadedForSite(true);
      setDeviceStatusesLoading(false);
      setSipStatusesLoaded(true);
      setSipStatusesLoading(false);
      setRefreshing(false);
      setLoading(false);

      // Refresh the DB-backed rows in the background without hitting the proxy again.
      // This keeps edits like name/IP changes reasonably fresh while avoiding the
      // expensive device and SIP status checks during quick back-and-forth navigation.
      setTimeout(() => {
        if (
          initialStatusLoadRunRef.current === runId &&
          currentSiteRef.current === siteId
        ) {
          loadDevices();
        }
      }, 0);

      return;
    }

    sipStatusesRef.current = {};
    sipFetchStartedRef.current = false;
    sipFetchInFlightRef.current = false;
    setDevices([]);
    setSelectedDevices([]);
    setDevicesLoadedForSite(false);
    setDeviceStatusesLoading(true);
    setSipStatusesLoaded(false);
    setSipStatusesLoading(true);
    setRefreshing(true);
    setLoading(true);

    const isCurrentInitialLoad = () => (
      initialStatusLoadRunRef.current === runId &&
      currentSiteRef.current === siteId
    );

    const runInitialStatusLoad = async () => {
      try {
        try {
          await triggerDeviceStatusCheck(siteId, getAuthToken());
        } catch (statusErr) {
          // Still load the table if the live status request fails or is skipped by cooldown.
          console.warn("Initial device status check failed or was skipped:", statusErr);
        }

        await new Promise((resolve) => setTimeout(resolve, 3500));

        if (!isCurrentInitialLoad()) return;

        await loadDevices();
        setDeviceStatusesLoading(false);

        if (!isCurrentInitialLoad()) return;

        await fetchSipStatusesForCurrentSite({ force: true });
      } catch (err) {
        console.error("Initial enterphones load failed:", err);
        if (isCurrentInitialLoad()) {
          setDevices([]);
          setIntercomsSummary({});
          setDeviceStatusesLoading(false);
        }
      } finally {
        if (!isCurrentInitialLoad()) return;

        setRefreshing(false);
        setDeviceStatusesLoading(false);
        setDevicesLoadedForSite(true);
        setSipStatusesLoading(false);
        setSipStatusesLoaded(true);
        setLoading(false);
      }
    };

    runInitialStatusLoad();
    // Intentionally keyed to siteId only so opening modals / syncing state does not
    // re-trigger a site-wide proxy staDtus check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  useEffect(() => {
    if (!siteId || devices.length === 0 || !devicesLoadedForSite) return;

    fetchSipStatusesForCurrentSite({
      blockActions: false,
    });
  }, [siteId, devices.length, devicesLoadedForSite, fetchSipStatusesForCurrentSite]);

  // CLEAR SELECTION ON SITE CHANGE
  useEffect(() => {
    setSelectedDevices([]);
  }, [siteId]);

  // load summary stats for header
  useEffect(() => {
    const loadSummary = async () => {
      try {
        const site = JSON.parse(localStorage.getItem("selectedSite"));
        const summary = await getIntercomsSummary(site?.site_uuid);
        setIntercomsSummary(summary);
        intercomsSummaryRef.current = summary || {};
      } catch (err) {
        console.error(err);
      }
    };

    loadSummary();
  }, []);

  useEffect(() => {
    if (syncing) return;
    if (!pendingSyncResults) return;

    setSyncResults(pendingSyncResults);
    setSyncResultsModal(true);
    setPendingSyncResults(null);
  }, [syncing, pendingSyncResults]);

  /*   // FAST UI REFRESH (DB POLL)
    useEffect(() => {
      if (!siteId) return;
  
      const interval = setInterval(loadDevices, 10_000); // every 10 seconds
      return () => clearInterval(interval);
    }, [siteId]); */

  // TRIGGER DEVICE STATUS CHECK
  const triggerStatusCheck = async () => {
    if (refreshing || syncing || isUpdatingBackground || discoveringIps || releaseDoorLoading || !siteId || livePreviewModal) return;

    clearIntercomsTableCache(siteId);
    setRefreshing(true);
    setDeviceStatusesLoading(true);
    markDeviceStatusRowsLoading();
    setDevicesLoadedForSite(false);
    setSipStatusesLoaded(false);
    setSipStatusesLoading(true);

    try {
      await triggerDeviceStatusCheck(siteId, getAuthToken());

      await new Promise((resolve) => setTimeout(resolve, 3000));

      if (livePreviewModal || syncing || isUpdatingBackground) return;

      await loadDevices();
      setDeviceStatusesLoading(false);

      // Refresh SIP registration too, instead of reusing the cached value.
      await fetchSipStatusesForCurrentSite({ force: true });
    } catch (err) {
      console.warn("Failed to trigger device status check", err);
      setDeviceStatusesLoading(false);

      try {
        await fetchSipStatusesForCurrentSite({ force: true });
      } catch (sipErr) {
        console.warn("Failed to refresh SIP statuses after status check failed:", sipErr);
      }
    } finally {
      setRefreshing(false);
      setDeviceStatusesLoading(false);
      setDevicesLoadedForSite(true);
      setSipStatusesLoading(false);
      setSipStatusesLoaded(true);
    }
  };

  const handleDiscoverIps = async () => {
    if (discoveringIps || refreshing || syncing || isUpdatingBackground || releaseDoorLoading || !siteId || livePreviewModal) return;

    clearIntercomsTableCache(siteId);
    setDiscoveringIps(true);

    try {
      const result = await discoverDeviceIps(siteId);

      const updatedCount = result?.updated_count ?? 0;
      const unchangedCount = result?.unchanged_count ?? 0;
      const unmatchedCount = result?.unmatched_count ?? 0;

      const knownCount =
        result?.known_count ??
        updatedCount + unchangedCount + unmatchedCount;

      const totalDeviceCount =
        result?.total_device_count ??
        devices.length ??
        knownCount;

      const skippedCount =
        result?.skipped_count ??
        Math.max(totalDeviceCount - knownCount, 0);

      const messageParts = [
        `IP discovery complete. Checked ${knownCount} of ${totalDeviceCount} enterphone${totalDeviceCount === 1 ? "" : "s"} with valid saved MAC address${knownCount === 1 ? "" : "es"}.`,
        `Updated: ${updatedCount}, unchanged: ${unchangedCount}, not found: ${unmatchedCount}.`,
      ];

      if (skippedCount > 0) {
        messageParts.push(
          `Skipped: ${skippedCount} missing/invalid MAC address${skippedCount === 1 ? "" : "es"}.`
        );
      }

      const bannerType =
        updatedCount > 0 || (knownCount > 0 && unmatchedCount === 0 && skippedCount === 0)
          ? "success"
          : "warning";

      showSyncBanner(
        messageParts.join(" "),
        bannerType,
        10000
      );

      await loadDevices();

      // After IPs are refreshed, ask devices for live status using their new IPs.
      setDevicesLoadedForSite(false);
      setDeviceStatusesLoading(true);
      markDeviceStatusRowsLoading();
      setSipStatusesLoaded(false);
      setSipStatusesLoading(true);

      try {
        await triggerDeviceStatusCheck(siteId, getAuthToken());
        await new Promise((resolve) => setTimeout(resolve, 3000));
        await loadDevices();
        setDeviceStatusesLoading(false);
        setDevicesLoadedForSite(true);
        await fetchSipStatusesForCurrentSite({ force: true });
      } catch (statusErr) {
        console.warn("Status check after IP discovery failed:", statusErr);
        setDeviceStatusesLoading(false);
        setDevicesLoadedForSite(true);

        try {
          await fetchSipStatusesForCurrentSite({ force: true });
        } catch (sipErr) {
          console.warn("SIP refresh after IP discovery failed:", sipErr);
        }
      }

    } catch (err) {
      console.error("IP discovery failed:", err);

      showSyncBanner(
        err.message || "IP discovery failed.",
        "danger",
        10000
      );

      await loadDevices();
    } finally {
      setDeviceStatusesLoading(false);
      setDiscoveringIps(false);
    }
  };

  // LIVE PREVIEW LOGIC
  const openLivePreview = (device) => {
    setPreviewDevice(device);
    setPreviewRefreshKey(Date.now());
    setForceFreshPreview(false);
    setInternalImgError(false);
    setShowInternalPreview(true);
    setLiveAutoRefreshEnabled(true);
    setLivePreviewModal(true);
  };

  const closeLivePreview = () => {
    setLivePreviewModal(false);
    setPreviewDevice(null);
    setForceFreshPreview(false);
    setInternalImgError(false);
    setShowInternalPreview(false);
    setPreviewRefreshKey(Date.now());
  };

  const refreshLivePreview = () => {
    setForceFreshPreview(true);
    setPreviewRefreshKey(Date.now());
    setInternalImgError(false);
    setShowInternalPreview(true);

    setTimeout(() => {
      setForceFreshPreview(false);
    }, 100);
  };

  useEffect(() => {
    if (!livePreviewModal || !previewDevice || !liveAutoRefreshEnabled) return;

    const interval = setInterval(() => {
      setForceFreshPreview(true);
      setPreviewRefreshKey(Date.now());
      setInternalImgError(false);
      setShowInternalPreview(true);
    }, 4000);

    return () => clearInterval(interval);
  }, [livePreviewModal, previewDevice, liveAutoRefreshEnabled]);

  useEffect(() => {
    if (syncing) return;
    if (!pendingSyncBanner) return;

    const { message, type, duration } = pendingSyncBanner;

    setSyncBanner({
      show: true,
      type,
      message,
    });

    window.clearTimeout(window.__hrmsSyncBannerTimeout);

    window.__hrmsSyncBannerTimeout = window.setTimeout(() => {
      setSyncBanner({
        show: false,
        type: "success",
        message: "",
      });
    }, duration || 4000);

    setPendingSyncBanner(null);
  }, [syncing, pendingSyncBanner]);

  // ----------------------------
  // DELETE LOGIC (UNCHANGED)
  // ----------------------------
  const toggleDeleteModal = (device = null) => {
    if (rowActionsBlocked && device) return;
    setDeviceToDelete(device);
    setDeleteModal(!deleteModal);
  };

  const handleDelete = async () => {
    if (!deviceToDelete || tableActionsBlocked) return;

    try {
      await deleteDevice(deviceToDelete.device_uuid);
      toggleDeleteModal();
      await loadDevices();
    } catch (err) {
      console.error(err);
      alert("Failed to delete device.");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDevices.length === 0 || tableActionsBlocked) return;

    try {
      await deleteDevices(selectedDevices);
      setSelectedDevices([]);
      await loadDevices();
    } catch (err) {
      console.error("Error deleting devices:", err);
      alert("Failed to delete selected devices.");
    }
  };

  const toggleSyncModal = (device = null) => {
    if (rowActionsBlocked && device) return;
    setDeviceToSync(device);
    setSyncModal((prev) => !prev);
  };

  const openReleaseDoorModal = (device) => {
    if (rowActionsBlocked) return;
    setDeviceToReleaseDoor(device);
    setReleaseDoorModal(true);
  };

  const closeReleaseDoorModal = () => {
    if (releaseDoorLoading) return;
    setReleaseDoorModal(false);
    setDeviceToReleaseDoor(null);
  };

  const handleReleaseDoor = async () => {
    if (!deviceToReleaseDoor?.device_uuid || tableActionsBlocked) return;

    const device = deviceToReleaseDoor;

    setReleaseDoorLoading(device.device_uuid);

    try {
      const result = await releaseDoor(device.device_uuid, {
        switchNumber: 1,
        reason: "intercom_table_action",
      });

      setReleaseDoorModal(false);
      setDeviceToReleaseDoor(null);

      showSyncBanner(
        result?.message ||
        `${device.device_name || "Enterphone"} door release triggered successfully.`,
        "success",
        8000
      );
    } catch (err) {
      console.error("Door release failed:", err);

      showSyncBanner(
        err.message || "Failed to release door.",
        "danger",
        10000
      );
    } finally {
      setReleaseDoorLoading(null);
    }
  };

  const handleSync = async (device) => {
    if (!device || !siteId || tableActionsBlocked) return;

    setSyncModal(false);
    setSyncLabel(
      device?.device_name
        ? `Syncing ${device.device_name}…`
        : "Syncing enterphone…"
    );
    syncingRef.current = true;
    setSyncing(true);

    const MIN_DURATION = 3000;
    const startTime = Date.now();

    try {
      const result = await syncDevice(device.device_uuid, siteId);

      const errors = result?.errors || [];
      const warnings = result?.warnings || [];
      const published = result ? [result] : [];

      const visibleErrors = filterVisibleSyncIssues(errors);
      const visibleWarnings = filterVisibleSyncIssues(warnings);
      const successfulDevices = getSuccessfulSyncItems(published, visibleErrors, visibleWarnings);

      if (visibleErrors.length || visibleWarnings.length) {
        openSyncResultsModal({
          title: "Enterphone Sync Results",
          summary: device?.device_name
            ? `Finished syncing ${device.device_name} with ${visibleErrors.length} error(s) and ${visibleWarnings.length} warning(s).`
            : `Finished syncing enterphone with ${visibleErrors.length} error(s) and ${visibleWarnings.length} warning(s).`,
          errors: visibleErrors,
          warnings: visibleWarnings,
          published,
        });
      } else if (successfulDevices.length) {
        showSyncBanner(
          device?.device_name
            ? `${device.device_name} synced successfully.`
            : "Enterphone synced successfully.",
          "success"
        );
      }

      await reloadDevicesAfterSync();
    } catch (err) {
      console.error(err);
      openSyncResultsModal({
        title: "Enterphone Sync Failed",
        summary: err.message || "Sync failed.",
        errors: err?.response?.data?.errors || [
          {
            device_uuid: device?.device_uuid,
            device_name: device?.device_name,
            device_ip: device?.device_ip,
            error: err?.response?.data?.detail || err.message || "Sync failed.",
          },
        ],
        warnings: [],
        published: [],
      });

      await reloadDevicesAfterSync();
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(MIN_DURATION - elapsed, 0);

      setTimeout(() => {
        releaseTableActionLocks();
        syncingRef.current = false;
        setSyncing(false);
        setSyncLabel("Syncing enterphones…");
      }, remaining);
    }
  };

  const handleBulkSync = async () => {
    if (!selectedSyncableDeviceIds.length || !siteId || tableActionsBlocked) return;

    setBulkSyncModal(false);
    setSyncLabel(`Syncing ${selectedSyncableDeviceIds.length} selected enterphone${selectedSyncableDeviceIds.length === 1 ? "" : "s"}…`);
    syncingRef.current = true;
    setSyncing(true);

    const MIN_DURATION = 3000;
    const startTime = Date.now();

    try {
      const result = await syncDevices(selectedSyncableDeviceIds, siteId);

      const errors = result?.errors || [];
      const warnings = result?.warnings || [];
      const published = result?.published || [];

      const visibleErrors = filterVisibleSyncIssues(errors);
      const visibleWarnings = filterVisibleSyncIssues(warnings);
      const successfulDevices = getSuccessfulSyncItems(published, visibleErrors, visibleWarnings);

      if (successfulDevices.length || visibleErrors.length || visibleWarnings.length) {
        openSyncResultsModal({
          title: "Selected Enterphones Sync Results",
          summary: `Successfully synced ${successfulDevices.length} enterphone${successfulDevices.length === 1 ? "" : "s"}. ${visibleErrors.length} device${visibleErrors.length === 1 ? "" : "s"} had error${visibleErrors.length === 1 ? "" : "s"} and ${visibleWarnings.length} warning${visibleWarnings.length === 1 ? "" : "s"}.`,
          errors: visibleErrors,
          warnings: visibleWarnings,
          published,
        });
      }

      await reloadDevicesAfterSync();
    } catch (err) {
      console.error(err);
      openSyncResultsModal({
        title: "Selected Enterphones Sync Failed",
        summary: err.message || "Bulk sync failed.",
        errors: [{ error: err.message || "Bulk sync failed." }],
        warnings: [],
        published: [],
      });

      await reloadDevicesAfterSync();
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(MIN_DURATION - elapsed, 0);

      setTimeout(() => {
        releaseTableActionLocks();
        syncingRef.current = false;
        setSyncing(false);
        setSelectedDevices([]);
        setSyncLabel("Syncing enterphones…");
      }, remaining);
    }
  };

  const handleSyncAllDevices = async () => {
    if (!syncableDevices.length || !siteId || tableActionsBlocked) return;

    setSyncAllModal(false);
    setSyncLabel(`Syncing ${syncableDevices.length} supported enterphone${syncableDevices.length === 1 ? "" : "s"}…`);
    syncingRef.current = true;
    setSyncing(true);

    const MIN_DURATION = 3000;
    const startTime = Date.now();

    try {
      const deviceUuids = syncableDevices
        .map((device) => device.device_uuid)
        .filter(Boolean);

      if (!deviceUuids.length) {
        console.warn("No device UUIDs found.");
        return;
      }

      const result = await syncAllDevices(deviceUuids, siteId);

      const errors = result?.errors || [];
      const warnings = result?.warnings || [];
      const published = result?.published || [];

      const visibleErrors = filterVisibleSyncIssues(errors);
      const visibleWarnings = filterVisibleSyncIssues(warnings);
      const successfulDevices = getSuccessfulSyncItems(published, visibleErrors, visibleWarnings);

      const failedDeviceCount = getUniqueIssueDeviceCount(visibleErrors, []);
      const warningDeviceCount = getUniqueIssueDeviceCount([], visibleWarnings);

      if (successfulDevices.length || visibleErrors.length || visibleWarnings.length) {
        openSyncResultsModal({
          title: "Sync All Enterphones Results",
          summary: formatSyncResultsSummary({
            successCount: successfulDevices.length,
            errorCount: visibleErrors.length,
            warningCount: visibleWarnings.length,
            errorDeviceCount: failedDeviceCount,
            warningDeviceCount,
          }),
          errors: visibleErrors,
          warnings: visibleWarnings,
          published,
        });
      }

      await reloadDevicesAfterSync();
    } catch (err) {
      console.error("Failed to sync all devices:", err);
      openSyncResultsModal({
        title: "Sync All Enterphones Failed",
        summary: err.message || "Sync all failed.",
        errors: [{ error: err.message || "Sync all failed." }],
        warnings: [],
        published: [],
      });

      await reloadDevicesAfterSync();
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(MIN_DURATION - elapsed, 0);

      setTimeout(() => {
        releaseTableActionLocks();
        syncingRef.current = false;
        setSyncing(false);
        setSyncLabel("Syncing enterphones…");
      }, remaining);
    }
  };

  const showSyncBanner = (message, type = "success", duration = 10000) => {
    setPendingSyncBanner({ message, type, duration });
  };

  const closeSyncBanner = () => {
    window.clearTimeout(window.__hrmsSyncBannerTimeout);
    setSyncBanner({
      show: false,
      type: "success",
      message: "",
    });
  };

  const handleTableWheel = useCallback((e) => {
    const el = tableScrollRef.current;
    if (!el) return;

    const hasHorizontalOverflow = el.scrollWidth > el.clientWidth;
    if (!hasHorizontalOverflow) return;

    // Allow true horizontal trackpad scrolling to behave normally,
    // but still keep it inside the table area.
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      e.preventDefault();
      e.stopPropagation();
      el.scrollLeft += e.deltaX;
      return;
    }

    // Always block page scrolling while cursor is over the table.
    e.preventDefault();
    e.stopPropagation();

    // Convert vertical mouse wheel movement into horizontal table scrolling.
    el.scrollLeft += e.deltaY;
  }, []);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    el.addEventListener("wheel", handleTableWheel, { passive: false });

    return () => {
      el.removeEventListener("wheel", handleTableWheel);
    };
  }, [handleTableWheel]);

  const selectRow = {
    mode: "checkbox",
    clickToSelect: false,
    selected: selectedDevices,
    nonSelectable: rowActionsBlocked
      ? (devices || []).map((row) => row.device_uuid).filter(Boolean)
      : [],
    onSelect: (row, isSelect) => {
      if (rowActionsBlocked) return false;

      setSelectedDevices((prev) =>
        isSelect
          ? [...prev, row.device_uuid]
          : prev.filter((id) => id !== row.device_uuid)
      );
    },
    onSelectAll: (isSelect, rows) => {
      if (rowActionsBlocked) return false;
      setSelectedDevices(isSelect ? rows.map((r) => r.device_uuid) : []);
    },
    classes: rowActionsBlocked ? "checkbox-column text-muted" : "checkbox-column",
    headerClasses: "checkbox-column-header",
  };

  const columns = [
    {
      dataField: "device_name",
      text: "Name",
      sort: true,
      formatter: (cell, row) => {
        const deviceImage = getDeviceImage(row.device_model);

        return (
          <Media className="align-items-center">
            <span
              className="avatar rounded-circle mr-3 d-flex align-items-center justify-content-center"
              style={deviceImage ? undefined : {
                backgroundColor: isDark ? "#374151" : "#e9ecef",
                color: isDark ? "#f8f9fa" : "#495057",
              }}
            >
              {deviceImage ? (
                <img alt={`${row.device_model || "Enterphone"} device`} src={deviceImage} />
              ) : (
                <i className="fas fa-tablet-alt" aria-hidden="true" />
              )}
            </span>
            <Media>
              <span className="name mb-0 text-sm">{cell}</span>
            </Media>
          </Media>
        );
      },
    },
    {
      dataField: "device_model",
      text: "Device Model",
      sort: true,
    },
    {
      dataField: "online_status",
      text: "Device Status",
      sort: true,
      formatter: (_, row) => {
        if (row.device_status_loading || deviceStatusesLoading) {
          return (
            <Badge color="" className="badge-dot mr-4">
              <i className="bg-warning" />
              <span className="status">Loading...</span>
            </Badge>
          );
        }

        const online =
          row.last_checkin &&
          Date.now() - new Date(row.last_checkin).getTime() <= 10 * 60 * 1000;

        return (
          <Badge color="" className="badge-dot mr-4">
            <i className={online ? "bg-success" : "bg-danger"} />
            <span className="status">{online ? "ONLINE" : "OFFLINE"}</span>
          </Badge>
        );
      },
    },
    {
      dataField: "last_checkin",
      text: "Last Device Checkin",
      sort: true,
      formatter: (cell) =>
        cell ? new Date(cell).toLocaleString() : "NEVER CONNECTED",
    },
    {
      dataField: "last_sync_success_at",
      text: "Last Synced At",
      sort: true,
      formatter: (cell) => {
        if (!cell) {
          return (
            <Badge color="" className="badge-dot mr-4">
              <i className="bg-secondary" />
              <span className="status">NEVER SYNCED</span>
            </Badge>
          );
        }

        const date = new Date(cell);
        if (Number.isNaN(date.getTime())) {
          return (
            <Badge color="" className="badge-dot mr-4">
              <i className="bg-secondary" />
              <span className="status">NEVER SYNCED</span>
            </Badge>
          );
        }

        return <span className="status">{date.toLocaleString()}</span>;
      },
    },
    {
      dataField: "config_sync_status",
      text: "Sync Status",
      sort: true,
      formatter: (cell, row) => {
        const { dotClass, label } = getSyncBadgeMeta(cell);

        return (
          <Badge color="" className="badge-dot mr-4">
            <i className={dotClass} />
            <span className="status">{label}</span>
          </Badge>
        );
      },
    },
    {
      dataField: "device_ip",
      text: "Device IP",
      sort: true,
      formatter: (cell) => cell || "N/A",
    },
    {
      dataField: "device_mac",
      text: "MAC Address",
      sort: true,
      formatter: (cell) => cell || "N/A",
    },
    {
      dataField: "sip_registration_status",
      text: "SIP Registration",
      sort: true,
      formatter: (cell) => {
        const value = cell || "N/A";
        const isOnline = value === "ONLINE";
        const isOffline = value === "OFFLINE";
        const isLoading = value === "Loading...";
        const isUnknown = value === "UNKNOWN";

        return (
          <Badge color="" className="badge-dot mr-4">
            <i
              className={
                isOnline
                  ? "bg-success"
                  : isOffline
                    ? "bg-danger"
                    : isLoading || isUnknown
                      ? "bg-warning"
                      : "bg-secondary"
              }
            />
            <span className="status">{value}</span>
          </Badge>
        );
      },
    },
    {
      dataField: "actions",
      text: "Actions",
      formatter: (_, row) => {
        if (!canPerformActions) return null;
        const currentActionsSupported = supportsCurrentDeviceActions(row.device_model);

        return (
          <div className="table-actions d-flex gap-2">
            {canSync && currentActionsSupported && (
              <Button
                size="sm"
                color="primary"
                disabled={rowActionsBlocked}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (rowActionsBlocked) return;
                  toggleSyncModal(row);
                }}
              >
                <i className="fas fa-sync-alt mr-1" />
                Sync
              </Button>
            )}

            {canSync && currentActionsSupported && (
              <Button
                size="sm"
                color="success"
                disabled={rowActionsBlocked || releaseDoorLoading === row.device_uuid}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (rowActionsBlocked) return;
                  openReleaseDoorModal(row);
                }}
              >
                <i
                  className={
                    releaseDoorLoading === row.device_uuid
                      ? "fas fa-circle-notch fa-spin mr-1"
                      : "fas fa-door-open mr-1"
                  }
                />
                {releaseDoorLoading === row.device_uuid ? "Releasing..." : "Release Door"}
              </Button>
            )}

            {canView && currentActionsSupported && (
              <Button
                size="sm"
                color="info"
                disabled={rowActionsBlocked}
                onMouseEnter={() => {
                  if (rowActionsBlocked) return;

                  prewarmDeviceCameraSnapshot(row.device_uuid, {
                    source: "internal",
                    width: 320,
                    height: 240,
                  }).catch((err) => {
                    console.warn("Hover prewarm failed:", err);
                  });
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (tableActionsBlocked) return;
                  openLivePreview(row);
                }}
              >
                <i className="fas fa-camera mr-1" />
                Live Preview
              </Button>
            )}

            {canDelete && (
              <Button
                size="sm"
                color="danger"
                disabled={rowActionsBlocked}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (rowActionsBlocked) return;
                  toggleDeleteModal(row);
                }}
              >
                <i className="fas fa-trash mr-1" />
                Delete
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <HusariaHeader name="Enterphones" parentName="HRMS" padding="9rem" />
      <Container fluid className={`mt--8 ${isDark ? "table-dark-mode" : ""}`}>
        {/* ===================== STATISTICS CARDS ===================== */}
        <Row className="mb-4">
          <Col lg="4" md="4" className="mb-3">
            <Card
              className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
              style={infoStyle}
            >
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p
                      className="mb-0"
                      style={{
                        fontSize: "0.875rem",
                        color: isDark ? "#adb5bd" : "#6c757d",
                        fontWeight: 500,
                      }}
                    >
                      Number of Intercoms
                    </p>
                    <h2 className="mb-0" style={{ fontWeight: 700 }}>
                      {intercomsSummary.intercoms ?? 0}
                    </h2>
                  </div>
                  <i className="fas fa-tablet-alt" style={{ fontSize: "2.5rem" }} />
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg="4" md="4" className="mb-3">
            <Card
              className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
              style={infoStyle}
            >
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p
                      className="mb-0"
                      style={{
                        fontSize: "0.875rem",
                        color: isDark ? "#adb5bd" : "#6c757d",
                        fontWeight: 500,
                      }}
                    >
                      Online Intercoms
                    </p>
                    <h2 className="mb-0" style={{ fontWeight: 700, color: "#28a745" }}>
                      {intercomsSummary.online ?? 0}
                    </h2>
                  </div>
                  <i className="fas fa-check-circle text-success" style={{ fontSize: "2.5rem" }} />
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg="4" md="4" className="mb-3">
            <Card
              className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
              style={infoStyle}
            >
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p
                      className="mb-0"
                      style={{
                        fontSize: "0.875rem",
                        color: isDark ? "#adb5bd" : "#6c757d",
                        fontWeight: 500,
                      }}
                    >
                      Offline Intercoms
                    </p>
                    <h2 className="mb-0" style={{ fontWeight: 700, color: "#dc3545" }}>
                      {intercomsSummary.offline ?? 0}
                    </h2>
                  </div>
                  <i className="fas fa-times-circle text-danger" style={{ fontSize: "2.5rem" }} />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <div className="col">
            <Card>
              {syncBanner.show && (
                <div
                  style={{
                    margin: "1rem 1rem 0 1rem",
                    padding: "0.85rem 1rem",
                    borderRadius: "0.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor:
                      syncBanner.type === "success"
                        ? (isDark ? "#1f3a2a" : "#ecfdf3")
                        : syncBanner.type === "danger"
                          ? (isDark ? "#3a1f1f" : "#fef2f2")
                          : (isDark ? "#3a321f" : "#fffbeb"),
                    border:
                      syncBanner.type === "success"
                        ? `1px solid ${isDark ? "#2f855a" : "#86efac"}`
                        : syncBanner.type === "danger"
                          ? `1px solid ${isDark ? "#b91c1c" : "#fca5a5"}`
                          : `1px solid ${isDark ? "#d69e2e" : "#fcd34d"}`,
                    color:
                      syncBanner.type === "success"
                        ? (isDark ? "#d1fae5" : "#166534")
                        : syncBanner.type === "danger"
                          ? (isDark ? "#fecaca" : "#991b1b")
                          : (isDark ? "#fef3c7" : "#92400e"),
                  }}
                >
                  <div style={{ fontWeight: 600 }}>
                    {syncBanner.message}
                  </div>

                  <Button
                    color="link"
                    onClick={closeSyncBanner}
                    style={{
                      padding: 0,
                      marginLeft: "1rem",
                      color: "inherit",
                      textDecoration: "none",
                      fontSize: "1.1rem",
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </Button>
                </div>
              )}
              <ToolkitProvider
                keyField="device_uuid"
                data={devices}
                columns={columns}
                search
              >
                {(props) => (
                  <>
                    <CardHeader
                      className="border-0 header-theme" style={{
                        backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                        color: isDark ? "#e2e8f0" : "#111",
                      }}>
                      <Row className="align-items-center">
                        <Col xs="6" className="d-flex align-items-center">

                          <Form className="navbar-search navbar-search-light form-inline mr-sm-3">
                            <FormGroup className="mb-0">
                              <InputGroup className="input-group-alternative input-group-merge search-border">
                                <InputGroupText>
                                  <i className="fas fa-search" style={{ color: isDark ? "#b0b0b0" : "rgb(102, 102, 102)" }} />
                                </InputGroupText>
                                <SearchBar
                                  className="form-control table-search-bar"
                                  placeholder="Search"
                                  {...props.searchProps}
                                />
                              </InputGroup>
                            </FormGroup>
                          </Form>
                        </Col>

                        <Col
                          xs="6"
                          className="d-flex justify-content-end align-items-center"
                          style={{ gap: "8px" }}
                        >
                          {tableActionsBlocked && !syncing && (
                            <span className="text-muted small d-flex align-items-center mr-2">
                              <i className="fas fa-circle-notch fa-spin mr-2" />
                              {tableActionBlockLabel}
                            </span>
                          )}

                          {selectedDevices.length > 0 && (
                            <>
                              {canSync && selectedSyncableDeviceIds.length > 0 && (
                                <Button
                                  className="btn-round btn-icon"
                                  color="primary"
                                  disabled={tableActionsBlocked}
                                  onClick={() => {
                                    if (tableActionsBlocked) return;
                                    setBulkSyncModal(true);
                                  }}
                                  size="sm"
                                >
                                  <span className="btn-inner--icon mr-1">
                                    <i className="fas fa-sync-alt" />
                                  </span>
                                  <span className="btn-inner--text">
                                    Sync Selected ({selectedSyncableDeviceIds.length})
                                  </span>
                                </Button>
                              )}

                              {canDelete && (
                                <Button
                                  className="btn-round btn-icon"
                                  color="danger"
                                  disabled={tableActionsBlocked}
                                  onClick={() => {
                                    if (tableActionsBlocked) return;
                                    setBulkDeleteModal(true);
                                  }}
                                  size="sm"
                                >
                                  <span className="btn-inner--icon mr-1">
                                    <i className="fas fa-trash" />
                                  </span>
                                  <span className="btn-inner--text">
                                    Delete Selected ({selectedDevices.length})
                                  </span>
                                </Button>
                              )}
                            </>
                          )}

                          {canSync && (
                            <Button
                              className="btn-round btn-icon"
                              color="primary"
                              size="sm"
                              disabled={tableActionsBlocked || !syncableDevices.length}
                              onClick={() => {
                                if (tableActionsBlocked) return;
                                setSyncAllModal(true);
                              }}
                            >
                              <span className="btn-inner--icon mr-1">
                                <i className="fas fa-sync-alt" />
                              </span>
                              <span className="btn-inner--text">
                                {syncableDevices.length === devices.length
                                  ? "Sync All"
                                  : `Sync Supported (${syncableDevices.length})`}
                              </span>
                            </Button>
                          )}

                          <Button
                            className="btn-round btn-icon"
                            color={isDark ? undefined : "secondary"}
                            size="sm"
                            disabled={tableActionsBlocked}
                            onClick={handleDiscoverIps}
                            style={{
                              backgroundColor: isDark ? "#374151" : undefined,
                              borderColor: isDark ? "#4b5563" : undefined,
                              color: isDark ? "#f8f9fa" : undefined,
                            }}
                          >
                            <span className="btn-inner--icon mr-1">
                              <i
                                className={discoveringIps ? "fas fa-circle-notch fa-spin" : "fas fa-network-wired"}
                                style={{ color: isDark ? "#f8f9fa" : undefined }}
                              />
                            </span>
                            <span
                              className="btn-inner--text"
                              style={{ color: isDark ? "#f8f9fa" : undefined }}
                            >
                              {discoveringIps ? "Discovering IPs…" : "Discover IPs"}
                            </span>
                          </Button>

                          <Button
                            className="btn-round btn-icon"
                            color="info"
                            size="sm"
                            disabled={tableActionsBlocked}
                            onClick={triggerStatusCheck}
                          >
                            <span className="btn-inner--icon mr-1">
                              <i className="fas fa-sync" />
                            </span>
                            <span className="btn-inner--text">Refresh</span>
                          </Button>

                          {canCreate && (
                            <>
                              <Button
                                className="btn-round btn-icon"
                                color="success"
                                id="new-device-btn"
                                disabled={tableActionsBlocked}
                                onClick={() => {
                                  if (tableActionsBlocked) return;
                                  navigate("/admin/hrms/add/device");
                                }}
                                size="sm"
                              >
                                <span className="btn-inner--icon mr-1">
                                  <i className="fas fa-plus" />
                                </span>
                                <span className="btn-inner--text">New</span>
                              </Button>

                              <UncontrolledTooltip delay={0} target="new-device-btn">
                                Add New Enterphone
                              </UncontrolledTooltip>
                            </>
                          )}
                        </Col>
                      </Row>
                    </CardHeader>

                    <div
                      ref={tableScrollRef}
                      className="py-4 table-responsive"
                    >
                      <BootstrapTable
                        {...props.baseProps}
                        bootstrap4
                        pagination={pagination}
                        bordered={false}
                        hover
                        rowStyle={{ cursor: canEdit && !rowActionsBlocked ? "pointer" : "default" }}
                        selectRow={selectRow}
                        rowEvents={{
                          onClick: (_, row) => {
                            if (!canEdit || rowActionsBlocked) return;
                            console.log("Navigating to edit:", row.device_uuid);
                            navigate(`/admin/hrms/edit/device/${row.device_uuid}`);
                          },
                        }}
                        classes="align-items-center table-flush"
                        noDataIndication={() =>
                          loading ? (
                            <div className="text-center">Loading...</div>
                          ) : (
                            <div className="text-center">
                              No enterphones found.
                            </div>
                          )
                        }
                      />
                    </div>
                  </>
                )}
              </ToolkitProvider>

              <DeleteDeviceModal
                isOpen={deleteModal}
                deviceName={deviceToDelete?.device_name}
                onCancel={toggleDeleteModal}
                onConfirm={handleDelete}
              />

              <SyncDeviceModal
                isOpen={syncModal}
                deviceName={deviceToSync?.device_name}
                onCancel={() => setSyncModal(false)}
                onConfirm={() => handleSync(deviceToSync)}
              />

              <SyncAllDevicesModal
                isOpen={syncAllModal}
                onCancel={() => setSyncAllModal(false)}
                onConfirm={() => handleSyncAllDevices()}
                deviceCount={syncableDevices.length}
                hasUnsupportedDevices={syncableDevices.length !== devices.length}
              />

              <Modal isOpen={releaseDoorModal} toggle={closeReleaseDoorModal} centered>
                <ModalHeader
                  style={{
                    backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                    color: isDark ? "#e2e8f0" : "#111",
                  }}
                  toggle={closeReleaseDoorModal}
                >
                  Release Door
                </ModalHeader>

                <ModalBody className="card-body">
                  Release the door relay for{" "}
                  <strong>
                    {deviceToReleaseDoor?.device_name || "this enterphone"}
                  </strong>
                  ?
                  <br />
                  <strong>This will immediately trigger switch 1 on the device.</strong>
                </ModalBody>

                <ModalFooter className="card-body">
                  <Button
                    color="light"
                    onClick={closeReleaseDoorModal}
                    disabled={!!releaseDoorLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="success"
                    onClick={handleReleaseDoor}
                    disabled={!!releaseDoorLoading || tableActionsBlocked}
                  >
                    {releaseDoorLoading ? "Releasing..." : "Release Door"}
                  </Button>
                </ModalFooter>
              </Modal>

              <Modal isOpen={bulkSyncModal} toggle={() => setBulkSyncModal(false)} centered>
                <ModalHeader
                  style={{
                    backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                    color: isDark ? "#e2e8f0" : "#111",
                  }}
                  toggle={() => setBulkSyncModal(false)}
                >
                  Sync Enterphones
                </ModalHeader>

                <ModalBody className="card-body">
                  Sync <strong>{selectedSyncableDeviceIds.length}</strong> selected enterphones?
                  <br />
                  <strong>This will publish directory updates to each selected enterphone.</strong>
                </ModalBody>

                <ModalFooter className="card-body">
                  <Button color="light" onClick={() => setBulkSyncModal(false)}>
                    Cancel
                  </Button>
                  <Button color="primary" disabled={tableActionsBlocked} onClick={handleBulkSync}>
                    Sync
                  </Button>
                </ModalFooter>
              </Modal>

              <Modal isOpen={livePreviewModal} toggle={closeLivePreview} centered size="xl">
                <ModalHeader
                  toggle={closeLivePreview}
                  style={{
                    backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                    color: isDark ? "#e2e8f0" : "#111",
                  }}
                >
                  Live Preview{previewDevice?.device_name ? ` - ${previewDevice.device_name}` : ""}
                </ModalHeader>

                <ModalBody className="card-body">
                  {previewDevice ? (
                    <>
                      <Row className="mb-3">
                        <Col className="d-flex justify-content-end" style={{ gap: "8px" }}>
                          <Button
                            color={liveAutoRefreshEnabled ? "success" : "secondary"}
                            size="sm"
                            onClick={() => setLiveAutoRefreshEnabled((prev) => !prev)}
                          >
                            <i className={`fas ${liveAutoRefreshEnabled ? "fa-pause" : "fa-play"} mr-1`} />
                            {liveAutoRefreshEnabled ? "Pause Live" : "Resume Live"}
                          </Button>

                          <Button color="info" size="sm" onClick={refreshLivePreview}>
                            <i className="fas fa-sync mr-1" />
                            Force Refresh
                          </Button>
                        </Col>
                      </Row>

                      <Row>
                        <Col md="12" className="mb-3">
                          <Card
                            className={isDark ? "bg-dark text-light border-dark" : ""}
                            style={{ overflow: "hidden" }}
                          >
                            <CardHeader
                              style={{
                                backgroundColor: isDark ? "#111827" : "#f8f9fa",
                                color: isDark ? "#e5e7eb" : "#111",
                                fontWeight: 600,
                              }}
                            >
                              Internal Camera
                            </CardHeader>

                            <CardBody className="text-center">
                              {!showInternalPreview ? (
                                <div className="text-muted py-5">
                                  Waiting to load internal camera snapshot...
                                </div>
                              ) : internalImgError ? (
                                <div className="text-muted py-5">
                                  Loading internal camera snapshot...
                                </div>
                              ) : (
                                <img
                                  src={getDeviceCameraSnapshotUrl(previewDevice.device_uuid, {
                                    source: "internal",
                                    width: 320,
                                    height: 240,
                                    fresh: forceFreshPreview,
                                    allowStale: !forceFreshPreview,
                                    cacheBust: previewRefreshKey,
                                  })}
                                  alt="Internal camera snapshot"
                                  style={{
                                    width: "100%",
                                    maxHeight: "480px",
                                    objectFit: "contain",
                                    borderRadius: "0.5rem",
                                  }}
                                  onLoad={() => {
                                    setInternalImgError(false);
                                  }}
                                  onError={() => {
                                    setInternalImgError(true);
                                  }}
                                />
                              )}
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>
                    </>
                  ) : null}
                </ModalBody>

                <ModalFooter className="card-body">
                  <Button color="light" onClick={closeLivePreview}>
                    Close
                  </Button>
                </ModalFooter>
              </Modal>

              <Modal isOpen={bulkDeleteModal} toggle={() => setBulkDeleteModal(false)} centered>
                <ModalHeader
                  style={{
                    backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                    color: isDark ? "#e2e8f0" : "#111",
                  }}
                  toggle={() => setBulkDeleteModal(false)}
                >
                  Delete Enterphones
                </ModalHeader>

                <ModalBody className="card-body">
                  Delete <strong>{selectedDevices.length}</strong> selected enterphones?
                  <br />
                  <strong>This action cannot be undone.</strong>
                </ModalBody>

                <ModalFooter className="card-body">
                  <Button color="light" onClick={() => setBulkDeleteModal(false)}>
                    Cancel
                  </Button>
                  <Button
                    color="danger"
                    disabled={tableActionsBlocked}
                    onClick={async () => {
                      if (tableActionsBlocked) return;
                      await handleBulkDelete();
                      setBulkDeleteModal(false);
                    }}
                  >
                    Delete
                  </Button>
                </ModalFooter>
              </Modal>

              <Modal isOpen={syncResultsModal} toggle={closeSyncResultsModal} centered size="lg">
                <ModalHeader
                  toggle={closeSyncResultsModal}
                  style={{
                    backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                    color: isDark ? "#e2e8f0" : "#111",
                  }}
                >
                  {syncResults.title || "Sync Results"}
                </ModalHeader>

                <ModalBody className="card-body">
                  {syncResults.summary ? (
                    <div className="mb-3">
                      {syncResults.summary}
                    </div>
                  ) : null}

                  {renderSyncResultAdvice()}

                  {syncResults.successes?.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-3 text-success">Successfully Synced</h5>
                      <div style={{ maxHeight: "220px", overflowY: "auto" }}>
                        {syncResults.successes.map((item, idx) => (
                          <div
                            key={`sync-success-${idx}`}
                            style={{
                              padding: "0.75rem",
                              marginBottom: "0.75rem",
                              borderRadius: "0.5rem",
                              backgroundColor: isDark ? "#1f2d1f" : "#f0fff4",
                              border: isDark ? "1px solid #2f5d2f" : "1px solid #b7ebc6",
                            }}
                          >
                            <div>
                              <strong>Device:</strong>{" "}
                              {item.device_name || item.device_ip || item.device_uuid || "Unknown device"}
                            </div>
                            {item.device_ip && (
                              <div>
                                <strong>IP:</strong> {item.device_ip}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {syncResults.errors?.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-3 text-danger">Errors</h5>
                      <div style={{ maxHeight: "220px", overflowY: "auto" }}>
                        {syncResults.errors.map((err, idx) => (
                          <div
                            key={`sync-error-${idx}`}
                            style={{
                              padding: "0.75rem",
                              marginBottom: "0.75rem",
                              borderRadius: "0.5rem",
                              backgroundColor: isDark ? "#2a1a1a" : "#fff5f5",
                              border: isDark ? "1px solid #5a2a2a" : "1px solid #f5c2c7",
                            }}
                          >
                            <div><strong>Device:</strong> {err.device_name || err.device_ip || err.device_uuid || "Unknown device"}</div>
                            {err.topic && <div><strong>Topic:</strong> {err.topic}</div>}
                            <div><strong>Message:</strong> {err.error || "Unknown error"}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {syncResults.warnings?.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-3 text-warning">Warnings</h5>
                      <div style={{ maxHeight: "220px", overflowY: "auto" }}>
                        {syncResults.warnings.map((warn, idx) => (
                          <div
                            key={`sync-warning-${idx}`}
                            style={{
                              padding: "0.75rem",
                              marginBottom: "0.75rem",
                              borderRadius: "0.5rem",
                              backgroundColor: isDark ? "#332a1a" : "#fff8e1",
                              border: isDark ? "1px solid #66521a" : "1px solid #ffe08a",
                            }}
                          >
                            <div><strong>Device:</strong> {warn.device_name || warn.device_ip || warn.device_uuid || "Unknown device"}</div>
                            {warn.topic && <div><strong>Topic:</strong> {warn.topic}</div>}
                            <div><strong>Message:</strong> {warn.error || "Unknown warning"}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {syncResults.errors?.length === 0 &&
                    syncResults.warnings?.length === 0 &&
                    syncResults.successes?.length === 0 && (
                      <div>No issues were reported.</div>
                    )}
                </ModalBody>

                <ModalFooter className="card-body">
                  <Button color="light" onClick={closeSyncResultsModal}>
                    Close
                  </Button>
                </ModalFooter>
              </Modal>

              {syncing && (
                <div
                  style={{
                    position: "fixed",
                    inset: 0,
                    backgroundColor: isDark ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.7)",
                    zIndex: 2000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Card
                    style={{
                      padding: "1.5rem 2rem",
                      borderRadius: "1rem",
                      backgroundColor: isDark ? "#2a2a2a" : "#ffffff",
                      color: isDark ? "#f8f9fa" : "#111827",
                      border: isDark ? "1px solid #3a3a3a" : "1px solid #dee2e6",
                      textAlign: "center",
                      minWidth: 260,
                    }}
                  >
                    <div className="spinner-border" role="status" />
                    <div className="mt-3" style={{ fontWeight: 600 }}>
                      {syncLabel || "Syncing enterphones…"}
                    </div>
                    <div style={{ opacity: 0.8, fontSize: "0.9rem" }}>
                      Please do not close this page.
                    </div>
                  </Card>
                </div>
              )}
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
}

export default HrmsDevices;